import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, ArrowRight } from "lucide-react";
import { fetchGoldRates, STORE } from "../lib/store";
import Seo, { jewelryStoreSchema, webPageSchema, faqSchema, breadcrumbSchema } from "../components/Seo";

const PATH = "/gold-rate-chicago";

const FAQS = [
  {
    q: "What is the difference between 22K and 18K gold?",
    a: "22K gold is about 91.6% pure gold, while 18K is 75%. 22K has a richer yellow color and is softer, while 18K is harder and is often used for diamond settings.",
  },
  {
    q: "How is gold jewelry priced?",
    a: "It is based on the day's gold rate and each piece's weight and design. Prices move with the gold market, which is why product prices update through the day.",
  },
  {
    q: "Why do prices change during the day?",
    a: "They follow the live gold rate, which changes throughout the day.",
  },
  {
    q: "Do you buy back gold?",
    a: "Please visit or call 773-262-4377 to ask about buy-back.",
  },
  {
    q: "Can I buy online?",
    a: "You can reserve online and pay in store, or inquire by phone or WhatsApp. There is no online checkout.",
  },
];

const KARATS = [
  { key: "k22", label: "22K", note: "91.6% pure" },
  { key: "k24", label: "24K", note: "99.9% pure" },
  { key: "k18", label: "18K", note: "75% pure" },
];

function chicagoTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/Chicago",
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    }) + " CT";
  } catch {
    return null;
  }
}

export default function GoldRateChicago() {
  const [rate, setRate] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let ok = true;
    fetchGoldRates().then((r) => ok && setRate(r)).catch(() => ok && setFailed(true));
    return () => { ok = false; };
  }, []);

  const updated = chicagoTime(rate?.updatedAt);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 pb-16" data-testid="gold-rate-page">
      <Seo
        title="Today's 22K Gold Rate in Chicago"
        description="Today's 22K gold rate at Regal Jewels on Devon Ave, Chicago, updated through the day. Reserve online, pay in store, or call 773-262-4377."
        path={PATH}
        jsonLd={[
          jewelryStoreSchema(),
          webPageSchema({
            name: "Today's 22K Gold Rate in Chicago",
            path: PATH,
            description: "Today's 22K gold rate at Regal Jewels on Devon Ave, Chicago, updated through the day.",
            dateModified: rate?.updatedAt || undefined,
          }),
          faqSchema(FAQS),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Today's 22K Gold Rate in Chicago", path: PATH },
          ]),
        ]}
      />

      <nav className="text-xs text-[#9c9482] mb-6" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-[#a8842c]">Home</Link> <span className="mx-1">/</span> Today's Gold Rate
      </nav>

      <p className="section-eyebrow">Devon Avenue, Chicago</p>
      <h1 className="section-title" data-testid="gold-rate-h1">Today's 22K Gold Rate in Chicago</h1>

      {/* Live rate card */}
      <div className="mt-8 border border-[#e6dcc5] bg-white" data-testid="gold-rate-card">
        <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#efe7d5]">
          {KARATS.map((k) => (
            <div key={k.key} className="p-6 text-center">
              <p className="text-xs uppercase tracking-[0.18em] text-[#a8842c]">{k.label} Gold</p>
              <p className="font-heading text-3xl text-[#171310] mt-2" data-testid={`rate-${k.key}`}>
                {rate ? `$${Number(rate.perGram[k.key]).toFixed(2)}` : failed ? "—" : "…"}
              </p>
              <p className="text-xs text-[#8f8571] mt-1">per gram · {k.note}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-[#efe7d5] px-6 py-3 text-center">
          {failed ? (
            <p className="text-sm text-[#8c2f1b]" data-testid="rate-updated">Rate temporarily unavailable — please call {STORE.phone} to confirm.</p>
          ) : (
            <p className="text-sm text-[#5c5240]" data-testid="rate-updated">
              Last updated: <span className="font-medium text-[#171310]">{updated || "loading…"}</span>
            </p>
          )}
        </div>
      </div>

      <p className="text-sm text-[#5c5240] mt-4" data-testid="rate-disclaimer">
        Rates change during the day. Please call or visit the store to confirm before you come in.
      </p>

      {/* CTAs */}
      <div className="flex flex-wrap gap-3 mt-7">
        <Link to="/shop" data-testid="rate-reserve-btn" className="inline-flex items-center gap-2 bg-[#171310] text-[#e9d9ac] px-6 py-3 text-xs tracking-[0.14em] uppercase hover:bg-[#3a2f1d] transition-colors">
          Reserve online, pay in store <ArrowRight size={15} />
        </Link>
        <Link to="/contact" data-testid="rate-inquire-btn" className="inline-flex items-center gap-2 border border-[#171310] text-[#171310] px-6 py-3 text-xs tracking-[0.14em] uppercase hover:bg-[#171310] hover:text-[#e9d9ac] transition-colors">
          Inquire
        </Link>
      </div>

      {/* Quick links */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-sm">
        <Link to="/collections/gold" className="text-[#a8842c] hover:underline">Browse 22K Gold Collection</Link>
        <Link to="/collections" className="text-[#a8842c] hover:underline">All Collections</Link>
        <a href={`tel:${STORE.phone}`} className="inline-flex items-center gap-1.5 text-[#a8842c] hover:underline"><Phone size={14} /> {STORE.phone}</a>
        <a href="https://www.google.com/maps/search/?api=1&query=Regal+Jewels+Inc+2625+W+Devon+Ave+Chicago+IL+60659" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#a8842c] hover:underline"><MapPin size={14} /> 2625 W. Devon Ave., Chicago</a>
      </div>

      {/* FAQ */}
      <section className="mt-14" data-testid="gold-rate-faq">
        <h2 className="font-heading text-2xl sm:text-3xl text-[#171310] mb-6">Gold Rate &amp; Pricing — FAQ</h2>
        <dl className="divide-y divide-[#efe7d5] border-t border-[#efe7d5]">
          {FAQS.map((f, i) => (
            <div key={i} className="py-5" data-testid={`faq-${i}`}>
              <dt className="font-medium text-[#171310]">{f.q}</dt>
              <dd className="text-[#5c5240] mt-2 leading-relaxed">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}
